import { useCallback, useEffect, useRef, useState } from 'react';

/** 滚动定位所需的最小分区描述：仅需一个 DOM id。 */
export interface SpySection {
  id: string;
}

/** 滚动定位缓存：分区顶部在内容坐标系下的偏移、分区高度、吸顶栏高度、滚动区尺寸。
 *  这些值只在布局变化（resize / 内容增减）时改变，滚动时不变，故缓存后滚动事件只需
 *  读 scrollTop 做算术，不再每次 getBoundingClientRect 触发 reflow。 */
export interface SpyGeometry {
  barOffset: number;
  tops: Array<{ id: string; top: number }>;
  heights: number[];
  scrollHeight: number;
  clientHeight: number;
}

export interface UseScrollSpyOptions {
  /** 滚动容器选择器（如 '.platform-app .main'） */
  root: string;
  /** 吸顶栏选择器，用于计算可视内容顶部偏移；缺省按 66px 估算 */
  bar?: string;
  /** 内容区选择器，ResizeObserver 监听其尺寸变化以失效缓存；缺省则不监听 */
  content?: string;
  /** 分区定义，按 DOM 出现顺序排列 */
  sections: ReadonlyArray<SpySection>;
  /** 是否在手动滚动时同步高亮；false 表示仅点击切换（点击即定位，不跟随滚动）。默认 true */
  syncOnScroll?: boolean;
}

export interface UseScrollSpyResult {
  activeSection: string;
  scrollToSection: (id: string) => void;
}

/** 测量并缓存滚动定位所需的全部几何信息。纯函数，不读任何外部状态，便于测试。 */
export function measureSpyGeometry(
  root: HTMLElement,
  bar: HTMLElement | null,
  sections: ReadonlyArray<SpySection>
): SpyGeometry {
  const rootTop = root.getBoundingClientRect().top;
  const tops: Array<{ id: string; top: number }> = [];
  const heights: number[] = [];
  for (const s of sections) {
    const el = document.getElementById(s.id);
    if (el) {
      tops.push({ id: s.id, top: el.getBoundingClientRect().top - rootTop + root.scrollTop });
      heights.push(el.offsetHeight);
    } else {
      tops.push({ id: s.id, top: 0 });
      heights.push(0);
    }
  }
  return {
    barOffset: bar ? bar.offsetHeight + 16 : 66,
    tops,
    heights,
    scrollHeight: root.scrollHeight,
    clientHeight: root.clientHeight,
  };
}

/** 计算当前应高亮的步骤，规则（与产品要求一致）：
 *  1) 某分区顶部滚动到可视区顶部（吸顶栏底）时 → 高亮它；
 *  2) 该分区顶部已滚出视口顶部（top < scrollTop），且下一分区顶部进入视口上半部分
 *     （next.top <= midLine）或整张可见（next.top >= topLine && next.bottom <= bottomLine）
 *     → 高亮让位给下一分区。
 * 点击步骤触发的滚动落点可能「被迫到底」（目标分区下方内容不足以把它推到顶部），
 * 此时规则会让位给下一分区；传入 prefId 时，若被点击分区仍可见则保持它，避免误让。
 * 纯函数，便于单测。 */
export function computeActiveSectionId(
  geom: SpyGeometry,
  scrollTop: number,
  sections: ReadonlyArray<SpySection>,
  prefId?: string
): string {
  const fallback = sections[0]?.id ?? '';
  const topLine = scrollTop + geom.barOffset; // 可视内容顶部（吸顶栏底）
  const midLine = scrollTop + geom.clientHeight / 2; // 视口中线 = 上半部分下界
  const bottomLine = scrollTop + geom.clientHeight; // 视口底部

  // 点击落点：被点击分区仍可见则保持，不被规则让给下一分区
  if (prefId) {
    const i = geom.tops.findIndex((t) => t.id === prefId);
    if (i >= 0) {
      const t = geom.tops[i].top;
      const b = t + geom.heights[i];
      if (t <= bottomLine && b >= topLine) return prefId;
    }
  }

  // 1) 贴顶分区：顶部已越过可视内容顶（top <= topLine）的最后一个
  let idx = 0;
  for (let i = 0; i < geom.tops.length; i++) {
    if (geom.tops[i].top <= topLine) idx = i;
    else break;
  }
  let active = sections[idx]?.id ?? fallback;

  // 2) 当前贴顶分区起始已滚出视口顶部，且下一分区起始进入上半部分或整张可见 → 让位
  while (idx + 1 < geom.tops.length) {
    const curExceeded = geom.tops[idx].top < scrollTop;
    const nextTop = geom.tops[idx + 1].top;
    const nextBottom = nextTop + geom.heights[idx + 1];
    const nextInUpperHalf = nextTop <= midLine;
    const nextFullyVisible = nextTop >= topLine && nextBottom <= bottomLine;
    if (curExceeded && (nextInUpperHalf || nextFullyVisible)) {
      idx++;
      active = sections[idx]?.id ?? active;
    } else {
      break;
    }
  }
  return active;
}

/** 滚动定位 composable：吸顶步骤栏 + 滚动 spy + 点击平滑滚动。
 *  返回当前高亮分区 id 与点击跳转函数。 */
export function useScrollSpy(options: UseScrollSpyOptions): UseScrollSpyResult {
  const {
    root: rootSel,
    bar: barSel,
    content: contentSel,
    sections,
    syncOnScroll = true,
  } = options;
  const [activeSection, setActiveSection] = useState(sections[0]?.id ?? '');
  const scrollLockRef = useRef(false);
  const scrollIdleTimerRef = useRef<number | null>(null);
  const clickedSectionRef = useRef<string | null>(null);
  const scrollMovedRef = useRef(false);
  const geomRef = useRef<SpyGeometry | null>(null);
  const topsMapRef = useRef<Map<string, number>>(new Map());

  // 测量并刷新几何缓存 + topsMap。挂载时与布局变化（resize）时调用，
  // 使点击跳转路径只需查表、不再触发 getBoundingClientRect。
  const refreshGeom = useCallback(
    (root: HTMLElement): SpyGeometry => {
      const geom = measureSpyGeometry(
        root,
        (barSel ? root.querySelector(barSel) : null) as HTMLElement | null,
        sections
      );
      geomRef.current = geom;
      const map = new Map<string, number>();
      for (const t of geom.tops) map.set(t.id, t.top);
      topsMapRef.current = map;
      return geom;
    },
    [barSel, sections]
  );

  const compute = useCallback(
    (prefId?: string): string => {
      const root = document.querySelector(rootSel) as HTMLElement | null;
      if (!root) return sections[0]?.id ?? '';
      if (!geomRef.current) refreshGeom(root);
      const geom = geomRef.current;
      if (!geom) return sections[0]?.id ?? '';
      return computeActiveSectionId(geom, root.scrollTop, sections, prefId);
    },
    [rootSel, refreshGeom, sections]
  );

  const scrollToSection = useCallback(
    (id: string) => {
      setActiveSection(id);
      clickedSectionRef.current = id;
      scrollMovedRef.current = false;
      // 锁定：点击步骤触发的滚动期间，不因中间经过的分区而覆盖高亮。
      // 解锁分两路：① 滚动事件停止 150ms（见 effect 内 recompute）；② 点击未引发任何滚动时由兜底定时器解锁。
      // 不用固定时长解锁——长距离平滑滚动耗时可能 >400ms，固定解锁会在动画中途释放锁，
      // 导致 onScroll 每帧 setActiveSection 走中间分区 → 整树重渲染 → 卡顿。
      scrollLockRef.current = true;
      if (scrollIdleTimerRef.current) window.clearTimeout(scrollIdleTimerRef.current);
      scrollIdleTimerRef.current = window.setTimeout(() => {
        // 兜底：仅当点击未引发任何滚动（目标已可见 / 已在顶部）时解锁；
        // 一旦发生过滚动，scrollMovedRef 为 true，此兜底作废，交由 scroll-idle 解锁。
        if (!scrollMovedRef.current) {
          scrollLockRef.current = false;
          setActiveSection(compute(clickedSectionRef.current ?? undefined));
        }
      }, 300);
      // 仅纵向滚动：直接设置滚动容器的 scrollTop，避免 scrollIntoView 触发横向滚动。
      // 目标 top 已在挂载/布局变化时预算并缓存（topsMapRef），点击时只做 O(1) 查表，不触发任何测量。
      const root = document.querySelector(rootSel) as HTMLElement | null;
      const el = document.getElementById(id);
      const geom = geomRef.current;
      const top = topsMapRef.current.get(id);
      if (root && el && geom && top != null) {
        // 减去吸顶栏高度，等价于 .ac-sec { scroll-margin-top } 留出的吸顶空间
        root.scrollTo({ top: Math.max(0, top - geom.barOffset), behavior: 'smooth' });
      } else {
        el?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    },
    [compute, rootSel]
  );

  // 滚动定位：手动滚动时高亮当前分区；点击步骤触发的滚动被锁定，不参与判定。几何信息缓存，滚动只读 scrollTop。
  useEffect(() => {
    const root = document.querySelector(rootSel) as HTMLElement | null;
    if (!root) return;

    refreshGeom(root);
    let rafId = 0;

    const recompute = () => {
      if (scrollLockRef.current) {
        // 仍在点击触发的滚动中：标记已发生滚动（抑制 scrollToSection 的兜底中途解锁），
        // 并在滚动停止 150ms 后解除锁定、按实际落点回填高亮。
        scrollMovedRef.current = true;
        if (scrollIdleTimerRef.current) window.clearTimeout(scrollIdleTimerRef.current);
        scrollIdleTimerRef.current = window.setTimeout(() => {
          scrollLockRef.current = false;
          // 点击落点若到底部，应高亮被点击的分区而非末分区
          setActiveSection(compute(clickedSectionRef.current ?? undefined));
        }, 150);
        return;
      }
      setActiveSection(compute());
    };

    const onScroll = () => {
      // 合并同一帧内的多次滚动事件，最多每帧计算一次
      if (rafId) return;
      rafId = window.requestAnimationFrame(() => {
        rafId = 0;
        recompute();
      });
    };

    // 布局变化（窗口缩放 / 表单内容增减导致分区高度变化）时重新测量并重算
    const invalidate = () => {
      refreshGeom(root);
      setActiveSection(compute());
    };

    let ro: ResizeObserver | null = null;
    const scrollEl = contentSel ? root.querySelector(contentSel) : null;
    if (scrollEl && typeof ResizeObserver !== 'undefined') {
      ro = new ResizeObserver(() => invalidate());
      ro.observe(scrollEl);
    }
    window.addEventListener('resize', invalidate);
    if (syncOnScroll) {
      root.addEventListener('scroll', onScroll, { passive: true });
    }
    setActiveSection(compute());

    return () => {
      if (syncOnScroll) root.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', invalidate);
      if (rafId) window.cancelAnimationFrame(rafId);
      if (scrollIdleTimerRef.current) window.clearTimeout(scrollIdleTimerRef.current);
      ro?.disconnect();
      geomRef.current = null;
      topsMapRef.current = new Map();
    };
  }, [rootSel, barSel, contentSel, sections, syncOnScroll, compute, refreshGeom]);

  return { activeSection, scrollToSection };
}
